import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {DataObjectId, Hub, User} from 'yougile-model'
import {Items} from '@api/model/items'
import {Sync} from '@client/sync'
import {DragData} from '@common/drag-data'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {ScriptsStore} from './scripts-store'

interface State {
  entityId: DataObjectId
  showUsers: boolean
  shownItemId: DataObjectId
}

export class ScriptsObjNavStore {
  state: State

  constructor(public store: ScriptsStore) {
    this.state = {
      entityId: undefined,
      showUsers: false,
      shownItemId: undefined
    }

    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        store: false
      },
      {autoBind: true}
    )
  }

  goTo(id: DataObjectId): void {
    if (id) {
      const box = Sync.getCurrentSandbox()
      if (Items.fromHub(box.get(id) as Hub).type === 'Task') {
        return
      }
    }
    this.state.entityId = id
  }

  goUp(): void {
    if (this.state.entityId) {
      if (this.state.entityId === this.store.globalState.companyId) {
        this.state.entityId = undefined
      } else {
        const box = Sync.getCurrentSandbox()
        this.state.entityId = box.get(this.state.entityId).location
      }
    }
    this.state.showUsers = false
    this.state.shownItemId = undefined
  }

  getType(id: DataObjectId): string {
    if (!id) {
      return ''
    }
    const box = Sync.getCurrentSandbox()
    const obj = Items.fromHub(box.get(id) as Hub)
    return obj ? obj.type.toLowerCase() : undefined
  }

  toggleShowInfo(id: DataObjectId, event: React.MouseEvent): void {
    event.stopPropagation()
    this.state.shownItemId = this.state.shownItemId === id ? undefined : id
  }

  currentTitle(): string {
    if (this.state.showUsers) {
      return 'Users'
    }
    if (!this.state.entityId) {
      return ''
    }
    const box = Sync.getCurrentSandbox()
    return box.get(this.state.entityId).title
  }

  listUsers(): User[] {
    const box = Sync.getCurrentSandbox()
    return box.getAllUsers(false)
  }

  list(): Hub[] {
    const box = Sync.getCurrentSandbox()
    return box.getChildrenHubs(this.state.entityId)
  }

  getCode(id: DataObjectId): string {
    const box = Sync.getCurrentSandbox()
    const entity = box.get(id)
    if (entity instanceof User) {
      return `var user = Users.get('${id}');`
    } else if (id === box.companyId) {
      return `var company = Items.get('${id}');`
    } else {
      const type = this.getType(id)
      return `var ${type} = Items.get('${id}');`
    }
  }

  insertIntoCode(id: DataObjectId): void {
    if (
      this.store.globalState.codeStore.state.openedTab &&
      this.store.globalState.monaco
    ) {
      if (!id) {
        return
      }
      this.store.globalState.monaco.insertAtCursor(this.getCode(id))
    } else {
      MessageBox.warn(Lang.msg.openEditorWarn)
    }
  }

  dragStart(code: string, e: React.DragEvent): void {
    DragData.set(e, {type: 'api-code', code})
  }

  setShowUsers(val: boolean): void {
    this.state.showUsers = val
  }
}

export const [ScriptsObjNavContext, useScriptsObjNavStore] =
  createUseStore(ScriptsObjNavStore)
